import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { getApiUrl } from '../utils/api';

const AdminAuthContext = createContext();

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(() => {
    // Initialize from localStorage to persist across HMR
    if (typeof window !== 'undefined') {
      const adminData = localStorage.getItem('adminData');
      if (!adminData) return null;
      try {
        return JSON.parse(adminData);
      } catch {
        // Corrupt value in storage — clear it so we don't crash on every load
        localStorage.removeItem('adminData');
        return null;
      }
    }
    return null;
  });
  const [loading, setLoading] = useState(true);
  const router = useRouter();
  const logoutInProgress = useRef(false);
  const authCheckInProgress = useRef(false);


  useEffect(() => {
    console.log('[DEBUG] AdminAuthContext: useEffect triggered on mount, admin:', !!admin);

    // Fetch CSRF token on mount
    fetchCSRFToken();

    const token = localStorage.getItem('adminToken');
    if (token && !authCheckInProgress.current) {
      console.log('[DEBUG] AdminAuthContext: Token exists, calling checkAuthStatus');
      checkAuthStatus();
    } else if (!token) {
      console.log('[DEBUG] AdminAuthContext: No token, setting loading false');
      setLoading(false);
    }
  }, []);

  const checkAuthStatus = async () => {
    if (authCheckInProgress.current) {
      console.log('[DEBUG] AdminAuthContext: checkAuthStatus already in progress, skipping');
      return;
    }
    authCheckInProgress.current = true;
    console.log('[DEBUG] AdminAuthContext: Starting auth check, admin:', !!admin);
    try {
      const token = localStorage.getItem('adminToken');
      const adminData = localStorage.getItem('adminData');

      if (token && adminData) {
        console.log('[DEBUG] AdminAuthContext: Token found, verifying with backend');
        // Verify token with backend
        const response = await fetch(getApiUrl('admin/profile'), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();
        if (data.success) {
          console.log('[DEBUG] AdminAuthContext: Token valid, setting admin');
          setAdmin(data.data);
        } else {
          console.log('[DEBUG] AdminAuthContext: Token invalid, logging out. Response:', data);
          logout();
        }
      } else {
        console.log('[DEBUG] AdminAuthContext: No token or adminData found');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Don't logout on network errors, only on invalid tokens
    } finally {
      setLoading(false);
      authCheckInProgress.current = false;
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(getApiUrl('admin/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('adminToken', data.data.token);
        localStorage.setItem('adminData', JSON.stringify(data.data.admin));
        setAdmin(data.data.admin);
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const logout = () => {
    console.log('[DEBUG] AdminAuthContext: logout called');
    if (logoutInProgress.current) return;
    logoutInProgress.current = true;
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    setAdmin(null);
    if (router.pathname !== '/admin') {
      router.push('/admin?logout=1');
    }
    setTimeout(() => {
      logoutInProgress.current = false;
    }, 1000); // Prevent multiple logouts within 1 second
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    const csrfToken = localStorage.getItem('csrfToken');

    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    return headers;
  };

  // Fetch and store CSRF token
  const fetchCSRFToken = async () => {
    try {
      const response = await fetch(getApiUrl('health'), {
        method: 'GET',
        credentials: 'include'
      });

      if (response.ok) {
        const csrfToken = response.headers.get('X-CSRF-Token');
        if (csrfToken) {
          localStorage.setItem('csrfToken', csrfToken);
        }
      }
    } catch (error) {
      console.warn('Failed to fetch CSRF token:', error);
    }
  };

  const value = {
    admin,
    loading,
    login,
    logout,
    getAuthHeaders,
    fetchCSRFToken,
    isAuthenticated: !!admin
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthContext;